import { createSelector } from 'reselect'

// Utils
import * as utils from 'Utils'

const DOCUMENT_DISPLAY_MAP = { folio: 'Hotel Folio', invoice: 'Invoice', receipt: 'Receipt' }
const FINANCIAL_FIELDS = ['Subtotal', 'Tax', 'Total']

const isFinancialField = ({ name }) => {
  return FINANCIAL_FIELDS.includes(name)
}

const getFieldName = (fieldName) => {
  return fieldName?.replace(/\[\d*\]\.+/, ` #${parseInt(fieldName?.replace(/\D/g, ''), 10) + 1} `)
}

const getDateString = (date) => {
  const parsedDate = new Date(Date.parse(date))
  return parsedDate?.toLocaleDateString(undefined, { timeZone: 'UTC' })
}

const getFinancialValues = (attributes = []) => {
  return attributes.reduce((acc, { type, value }) => {
    return isFinancialField(type) ? { ...acc, [type.name.toLowerCase()]: value } : acc
  }, {})
}

const isSelectedFilter =
  (filter) =>
  ({ id }) => {
    if (Array.isArray(filter)) {
      return filter.includes(id)
    }
    return filter === id
  }

const typeMap = {
  audit: 'audit-log',
  expensedetail: 'expense-details',
  compliance: 'risk-score',
}

const getFormattedValue = (fieldType, value, currencySymbol) => {
  switch (fieldType) {
    case 'datetime':
      return value ? new Date(Date.parse(value)).toLocaleDateString() : null

    case 'string':
      return value

    case 'decimal': {
      const parsedValue = parseFloat(value).toFixed(2)
      return parsedValue === 'NaN' ? value : `${currencySymbol} ${parsedValue}`
    }

    default:
      return value
  }
}

const getMappedFilters = (filters, filterOptions) => {
  const strippedFilters = utils.getStrippedFilters(filters)
  const mappedFilters = Object.entries(strippedFilters).reduce((acc, [key, value]) => {
    if (!Array.isArray(value) && key !== 'paymentStatuses') {
      return { ...acc, [key]: value }
    }
    const options = filterOptions[key]
    if (key === 'paymentStatuses') {
      return { ...acc, [key]: options?.filter(isSelectedFilter(value))[0] }
    }
    if (!options) {
      return acc
    }
    return { ...acc, [key]: options.filter(isSelectedFilter(value)) }
  }, {})
  return mappedFilters
}

export const getValueFromMapping = (name, tagType, mappings = []) => {
  const mapping = mappings.find((item) => {
    return item[tagType]?.type.name === name
  })

  if (mapping) {
    return mapping[tagType]?.value
  }
}
const formatAttributes = (attributes) => {
  let atts = {}
  attributes.forEach(({ type: { name }, value }) => {
    name = name[0].toLowerCase() + name.slice(1)
    atts[name] = value
  })
  return atts
}

const getAuditLogs = ({ reports }) => ({
  auditLogs: reports.auditLogs,
  next: reports.next,
  previous: reports.previous,
})
const getCreatingExportReport = ({ reports }) => reports.creatingExportReport
const getCreatingTransactionReport = ({ reports }) => reports.createTransactionReport
const getCreatingReport = ({ reports }) => reports.creatingReport
const getFetchingAuditLogs = ({ reports }) => reports.fetchingAuditLogs
const getFetchingReportExpenses = ({ reports }) => reports.fetchingReportExpenses
const getFetchingReports = ({ reports }) => reports.fetchingReports
const getReportDownloadURL = ({ reports }) => reports.reportDownloadURL
const getReportExportDownloadURL = ({ reports }) => reports.reportExportDownloadURL
const getReportTransactionDownloadURL = ({ reports }) => reports.reportTransactionDownloadURL
const getReportExpenses = ({ reports }) => ({
  next: reports.nextReportExpenses,
  previous: reports.previousReportExpenses,
  reportExpenses: reports.reportExpenses,
})
const getReports = ({ reports }) => ({
  next: reports.next,
  previous: reports.previous,
  reports: reports.reports,
})
const getUpdatingReport = ({ reports }) => reports.updatingReport || reports.deletingReport

const reportSelectors = {
  getAuditLogs: createSelector([getAuditLogs], ({ auditLogs, next, previous }) => ({
    auditLogs: auditLogs.map(
      ({
        actorDatetime,
        currencySymbol,
        fieldName,
        fieldType,
        oldValue,
        newValue,
        ...auditLog
      }) => {
        const formattedNewValue = getFormattedValue(fieldType, newValue, currencySymbol)
        const formattedOldValue = getFormattedValue(fieldType, oldValue, currencySymbol)
        return {
          ...auditLog,
          actorDatetime: `${new Date(Date.parse(actorDatetime)).toLocaleDateString()} ${new Date(
            Date.parse(actorDatetime),
          ).toLocaleTimeString()}`,
          change:
            oldValue === null || !fieldName
              ? 'Itemize System'
              : `Field: ${getFieldName(fieldName)}`,
          currencySymbol,
          newValue: formattedNewValue,
          oldValue: formattedOldValue,
        }
      },
    ),
    next,
    previous,
  })),
  getCreatingReport: createSelector([getCreatingReport], (creatingReport) => creatingReport),
  getFetchingAuditLogs: createSelector(
    [getFetchingAuditLogs],
    (fetchingAuditLogs) => fetchingAuditLogs,
  ),
  getFetchingReportExpenses: createSelector(
    [getFetchingReportExpenses],
    (fetchingReportExpenses) => fetchingReportExpenses,
  ),
  getFetchingReports: createSelector([getFetchingReports], (fetchingReports) => fetchingReports),
  getReportDownloadURL: createSelector(
    [getReportDownloadURL],
    (reportDownloadURL) => reportDownloadURL,
  ),
  getReportExportDownloadURL: createSelector(
    [getReportExportDownloadURL],
    (reportExportDownloadURL) => reportExportDownloadURL,
  ),
  getReportTransactionDownloadURL: createSelector(
    [getReportTransactionDownloadURL],
    (reportTransactionDownloadURL) => reportTransactionDownloadURL,
  ),
  getReportExpenses: createSelector([getReportExpenses], ({ next, previous, reportExpenses }) => {
    return {
      expenses: reportExpenses.map(
        ({ documentType, merchant, orders, tagMappings, ...expense }) => ({
          ...expense,
          currency: orders[0]?.currency?.symbol,
          documentType: DOCUMENT_DISPLAY_MAP[documentType.name],
          expenseCategory:
            orders[0]?.expenseCategory?.name ||
            getValueFromMapping('ExpenseCategory', 'toTag', tagMappings)?.name ||
            merchant.category.expenseCategory?.name,
          glCode: orders[0]?.glCode?.value || getValueFromMapping('GLcode', 'toTag', tagMappings),
          merchant: merchant.name,
          merchantImage: merchant.image || merchant.category.image,
          paymentStatus: orders[0]?.paymentStatus?.value.toLowerCase(),
          riskScore: expense.riskScore || 'low',
          ...getFinancialValues(orders[0]?.attributes),
          ...formatAttributes(orders[0]?.attributes),
          exceptionCount: expense?.exceptions?.length,
        }),
      ),
      next,
      previous,
    }
  }),
  getReports: (filterOptions) =>
    createSelector([getReports], ({ next, previous, reports }) => ({
      next,
      previous,
      reports: reports.map(({ createdDate, filters, templateName, ...report }) => ({
        ...report,
        createdDate: getDateString(createdDate),
        filters: getMappedFilters(filters, filterOptions),
        startDate: filters?.transactionDate?.min,
        endDate: filters?.transactionDate?.max,
        expenseColumns: filters?.columns,
        type: typeMap[templateName],
      })),
    })),
  getUpdatingReport: createSelector([getUpdatingReport], (updatingReport) => updatingReport),
  getCreatingExportReport: createSelector(
    [getCreatingExportReport],
    (creatingExportReport) => creatingExportReport,
  ),
  getCreatingTransactionReport: createSelector(
    [getCreatingTransactionReport],
    (createTransactionReport) => createTransactionReport,
  ),
}

export default reportSelectors
